import { AsYouType } from 'libphonenumber-js';
import { styled } from 'styled-components';
import moment from 'moment-timezone';

import * as api from '../../../../../../api';
import ProviderImage from '../../../../../ProviderImage';
import { getProviderNameFull } from '../../../../../../utils/provider';
import {
  getCancelTimeWindow,
  isAppointmentCancellableNow,
} from '../../../../../../utils/cancelReschedule';

import { Alert } from '@vivaahealth/design-system';

const DEFAULT_PREVISIT_INSTRUCTIONS =
  'Please arrive at the arrival time listed above. We ask that you bring your proof of insurance.';

export enum Status {
  Cancelled = 'CANCELLED',
  Rescheduled = 'RESCHEDULED',
}

export type AppointmentDetails = {
  startTime: Date;
  practice: api.Practice.SafePractice;
  provider?: Omit<api.User.SafeProvider, 'sex' | 'virtualVisitUrl'>;
  isCancelEnabled?: boolean;
  isReschedulingEnabled?: boolean;
  previsitConfirmCancelMinimumCancelTime?: api.PracticePreference.TimeLookahead | null;
};

interface Props {
  appointment: AppointmentDetails;
  onActionButtonClicked?: () => void;
  actionButtonLabel?: string;
  disabled: boolean;
  status?: Status;
  previsitInstructions?: string;
}

const Appointment: React.FC<Props> = ({
  appointment,
  onActionButtonClicked,
  actionButtonLabel,
  disabled,
  status,
  previsitInstructions,
}) => {
  const isCancellableNow = isAppointmentCancellableNow(appointment);
  const cancelTimeLimit = getCancelTimeWindow(appointment);
  const renderAppointmentInfo = (appointment: AppointmentDetails) => {
    const { practice, provider } = appointment;
    const previsitInstructionsContent = (
      <>
        <strong>Previsit instructions:</strong>
        <p>{previsitInstructions ?? DEFAULT_PREVISIT_INSTRUCTIONS}</p>
      </>
    );
    if (provider) {
      return (
        <>
          <ProviderImage provider={provider} />
          <ProviderInfo>
            <ProviderName>{getProviderNameFull({ provider })}</ProviderName>
            <PracticeName>{practice.name}</PracticeName>
            <PhoneNumber href={`tel:${practice.phoneNumber}`}>
              {new AsYouType('US').input(practice.phoneNumber)}
            </PhoneNumber>
            <PrevisitInstructions>
              {previsitInstructionsContent}
            </PrevisitInstructions>
            {actionButtonLabel ? (
              <AppointmentAction
                type="button"
                disabled={!isCancellableNow || disabled}
                onClick={() => onActionButtonClicked && onActionButtonClicked()}
              >
                {actionButtonLabel}
              </AppointmentAction>
            ) : null}
          </ProviderInfo>
        </>
      );
    }
    return (
      <>
        <ProviderImage
          provider={{
            firstName: practice.name,
            lastName: '',
            previsitImageUrl: '',
          }}
        />
        <ProviderInfo>
          <ProviderName>{practice.name}</ProviderName>
          <PhoneNumber href={`tel:${practice.phoneNumber}`}>
            {new AsYouType('US').input(practice.phoneNumber)}
          </PhoneNumber>
          <PrevisitInstructions>
            {previsitInstructionsContent}
          </PrevisitInstructions>
          {actionButtonLabel ? (
            <AppointmentAction
              type="button"
              disabled={!isCancellableNow || disabled}
              onClick={() => onActionButtonClicked && onActionButtonClicked()}
            >
              {actionButtonLabel}
            </AppointmentAction>
          ) : null}
        </ProviderInfo>
      </>
    );
  };

  return (
    <AppointmentWrapper data-test-id="appointment-card">
      <AppointmentHeader>
        <strong>
          {moment(appointment.startTime)
            .tz(appointment.practice.timezone)
            .format('MMM Do, h:mma')}
        </strong>
        {status ? (
          <AppointmentStatus $isDanger={status === Status.Cancelled}>
            {status === Status.Cancelled ? 'Cancelled' : 'New'}
          </AppointmentStatus>
        ) : null}
      </AppointmentHeader>
      <AppointmentContent>
        {renderAppointmentInfo(appointment)}
      </AppointmentContent>
      {!!actionButtonLabel && !isCancellableNow && (
        <StyledAlert variant="danger" title={'Cancellation window has passed'}>
          Cancelling appointments within {cancelTimeLimit} will require a call
          to the clinic listed above.
        </StyledAlert>
      )}
    </AppointmentWrapper>
  );
};

const AppointmentWrapper = styled.div`
  border: 1px solid var(--border-color);
  border-radius: 4px;
  font-size: 14px;
  margin-top: 10px;
`;

const AppointmentStatus = styled.span.attrs<{ $isDanger: boolean }>(
  (props) => ({
    $isDanger: props.$isDanger ?? false,
  }),
)`
  font-weight: 600;
  color: ${(props) =>
    props.$isDanger
      ? 'var(--color-state-danger)'
      : 'var(--color-state-success)'};
`;

const AppointmentHeader = styled.div`
  background-color: #f7f7f7;
  padding: 10px 20px;
  border-radius: 4px 4px 0 0;
  display: flex;
  justify-content: space-between;
  align-items: center;
`;

const AppointmentContent = styled.div`
  padding: 20px;
  display: flex;
`;

const ProviderInfo = styled.div`
  margin-left: 1.5rem;
`;

const ProviderName = styled.strong`
  font-size: 16px;
  margin-bottom: 0.3rem;
  display: block;
`;

const PhoneNumber = styled.a`
  color: var(--font-color);
  text-decoration: none;
  font-size: 16px;

  &:hover {
    color: var(--action-color);
  }
`;

const AppointmentAction = styled.button`
  display: block;
  margin-top: 1rem;
  border: none;
  background-color: transparent;
  color: var(--action-color);
  font-weight: 600;
  padding: 0;

  &:disabled {
    color: var(--color-hint);
  }
`;

const StyledAlert = styled(Alert)`
  margin: 0 20px 20px 20px;
`;

const PracticeName = styled.div`
  font-size: 16px;
`;

const PrevisitInstructions = styled.div`
  margin-top: 0.5rem;
  font-size: 16px;
  color: var(--font-color);
`;

export default Appointment;
